import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  fetchFilesTypesList,
  setDestroy,
} from "containers/files-types/files-types-container.slice";
import {
  FILE_NAME_PLACEHOLDERS,
  previewFileName,
} from "containers/files-types/file-name-rule";
import {
  createFilesTypes,
  updateFilesTypes,
} from "services/files-types.service";
import {
  CatalogTable,
  ChangeLink,
  EditPanel,
  SettingsCard,
  useSaveWithToast,
  useScrollIntoViewOnOpen,
} from "containers/settings/ui";
import {
  AddButton,
  Field,
  FIELD_INPUT_CLASS,
  PanelHeading,
} from "containers/settings/catalog-form-parts";

type FilesTypeDraft = {
  id?: string;
  label: string;
  template: string;
  description: string;
};

const NEW_FILES_TYPE: FilesTypeDraft = {
  label: "",
  template: "",
  description: "",
};

/** The list of `{placeholders}` a rule may use, as a help text under the rule field. */
function PlaceholderHelp() {
  const { t } = useTranslation();

  return (
    <div className="mt-2 space-y-1">
      <p>{t("files_types_rule_help")}</p>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
        {FILE_NAME_PLACEHOLDERS.map((placeholder) => (
          <div key={placeholder.path} className="contents">
            <dt>
              <code className="text-gray-900">{`{${placeholder.path}}`}</code>
            </dt>
            <dd>{t(placeholder.descriptionKey)}</dd>
          </div>
        ))}
      </dl>
      <p>{t("files_types_rule_template_fields")}</p>
    </div>
  );
}

type FilesTypeEditPanelProps = {
  saved: FilesTypeDraft;
  companyId: string;
  onSaved: () => void;
  onCancel: () => void;
};

function FilesTypeEditPanel({
  saved,
  companyId,
  onSaved,
  onCancel,
}: FilesTypeEditPanelProps) {
  const { t } = useTranslation();
  const { save, saving, error } = useSaveWithToast();
  const [draft, setDraft] = useState(saved);
  const [triedToSave, setTriedToSave] = useState(false);

  const label = draft.label.trim();
  const template = draft.template.trim();

  const submit = async () => {
    setTriedToSave(true);
    if (!label || !template) return;

    const payload = {
      id: draft.id,
      label,
      template,
      description: draft.description,
    };
    const wasSaved = await save(() =>
      draft.id
        ? updateFilesTypes(payload, companyId)
        : createFilesTypes(payload, companyId)
    );
    if (wasSaved) onSaved();
  };

  return (
    <EditPanel
      onSave={submit}
      onCancel={onCancel}
      saving={saving}
      error={triedToSave && (!label || !template) ? t("requiredFields") : error}
    >
      <PanelHeading>
        {saved.id
          ? `${t("settings_change")}: ${saved.label}`
          : t("files_types_add")}
      </PanelHeading>
      <div className="space-y-3">
        <Field
          htmlFor="files-type-label"
          label={t("files_types_document")}
          hint={t("files_types_document_hint")}
        >
          <input
            id="files-type-label"
            className={FIELD_INPUT_CLASS}
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            autoComplete="off"
            autoFocus
          />
        </Field>
        <Field
          htmlFor="files-type-template"
          label={t("files_types_rule")}
          hint={<PlaceholderHelp />}
        >
          <input
            id="files-type-template"
            className={`${FIELD_INPUT_CLASS} font-mono`}
            value={draft.template}
            onChange={(e) => setDraft({ ...draft, template: e.target.value })}
            autoComplete="off"
          />
          {template && (
            <p className="mt-1.5 text-sm text-gray-900">
              <span className="text-gray-600">
                {t("files_types_rule_example")}:{" "}
              </span>
              {previewFileName(template)}
            </p>
          )}
        </Field>
        <Field htmlFor="files-type-description" label={t("description")}>
          <textarea
            id="files-type-description"
            className={FIELD_INPUT_CLASS}
            rows={2}
            value={draft.description}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value })
            }
          />
        </Field>
      </div>
    </EditPanel>
  );
}

function FilesTypesContainer() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const { collection, isLoading, isError } = useAppSelector(
    (state) => state.filesTypesReducer
  );
  const [editing, setEditing] = useState<FilesTypeDraft | null>(null);
  const panelRef = useScrollIntoViewOnOpen(editing);

  useEffect(() => {
    if (companyId) dispatch(fetchFilesTypesList(companyId));
    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, dispatch]);

  if (!companyId) return null;

  const startEdit = (id: string) => {
    const row = collection.find((filesType) => filesType.id === id);
    if (row) {
      setEditing({
        id: row.id,
        label: row.label,
        template: row.template,
        description: row.description ?? "",
      });
    }
  };

  const closeAndReload = () => {
    setEditing(null);
    dispatch(fetchFilesTypesList(companyId));
  };

  return (
    <SettingsCard
      title={t("settings_nav_file_names_label")}
      description={t("files_types_card_description")}
      actions={
        <AddButton
          label={t("files_types_add")}
          onClick={() => setEditing(NEW_FILES_TYPE)}
        />
      }
    >
      {editing && (
        <div ref={panelRef} className="mb-4">
          <FilesTypeEditPanel
            key={editing.id ?? "new"}
            saved={editing}
            companyId={companyId}
            onSaved={closeAndReload}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}
      <CatalogTable
        columns={[
          { key: "label", header: t("files_types_document") },
          { key: "template", header: t("files_types_rule") },
          { key: "description", header: t("description") },
        ]}
        rows={collection.map((filesType) => ({
          id: filesType.id,
          cells: {
            label: <span className="font-medium">{filesType.label}</span>,
            template: <code className="text-xs">{filesType.template}</code>,
            description: filesType.description,
          },
        }))}
        renderRowAction={(id) => <ChangeLink onClick={() => startEdit(id)} />}
        empty={t("files_types_empty")}
        isLoading={isLoading}
        loadError={isError ? t("settings_load_failed") : undefined}
      />
    </SettingsCard>
  );
}

export default FilesTypesContainer;
